import { c as _c } from "react/compiler-runtime";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api, type InfrastructureCosts, type IntegrationCheckResult, type IntegrationState, type IntegrationStatus } from "@/api";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn, formatDateTime, formatPrice } from "@/lib/utils";
import { useAuth } from "@/store";
export const integrationKeys = {
  overview: ["integrations", "overview"] as const,
  costs: ["integrations", "costs"] as const
};
const GROUP_ORDER = ["payments", "delivery", "messaging", "data", "ai", "analytics", "accounting", "infrastructure", "documents"] as const;

/** У этих карточек нет своих обращений наружу — «последней операции» не бывает. */
const NO_ACTIVITY = new Set(["balance", "sbp", "metrika", "edo"]);
const STATUS_STYLE: Record<IntegrationStatus, string> = {
  connected: "border-emerald-600/30 bg-emerald-600/10 text-emerald-800 dark:text-emerald-300",
  test_mode: "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300",
  no_keys: "border-destructive/40 bg-destructive/10 text-destructive",
  inactive: "border-slate-400/40 bg-slate-400/10 text-slate-700 dark:text-slate-300",
  disabled: "border-slate-400/40 bg-slate-400/10 text-slate-700 dark:text-slate-300",
  not_connected: "border-dashed border-slate-400/50 bg-transparent text-muted-foreground"
};

/**
 * Все внешние сервисы площадки одним экраном: по карточке на интеграцию,
 * сгруппированные по назначению. Над карточками — сводка по платежам: кто
 * РЕАЛЬНО принимает оплату и чем это отличается от PAYMENT_PROVIDER.
 */
export function IntegrationsOverviewSection() {
  const $ = _c(113);
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp);
  let t0;
  if ($[0] !== user) {
    t0 = hasAdminPermission(user, "action.integrations.manage");
    $[0] = user;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canManage = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: integrationKeys.overview,
      queryFn: _temp2,
      staleTime: 30000
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const overview = useQuery(t1);
  if (overview.isPending) {
    let t2;
    if ($[3] !== t) {
      t2 = t("integrations.overview.loading");
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t2) {
      t3 = <p className="text-sm text-muted-foreground">{t2}</p>;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (overview.isError || !overview.data) {
    const t2 = overview.error instanceof Error ? overview.error.message : "";
    let t3;
    if ($[7] !== t || $[8] !== t2) {
      t3 = t("integrations.overview.loadError", {
        error: t2
      });
      $[7] = t;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== t3) {
      t4 = <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{t3}</div>;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  const {
    payments,
    items,
    environment
  } = overview.data;
  let t2;
  let t3;
  let t4;
  if ($[12] !== canManage || $[13] !== environment || $[14] !== items || $[15] !== payments.activeProvider || $[16] !== payments.configuredProvider || $[17] !== payments.testMode || $[18] !== payments.warnings || $[19] !== t) {
    let t5;
    if ($[23] !== items) {
      t5 = group => ({
        group,
        items: items.filter(item => item.group === group)
      });
      $[23] = items;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const groups = GROUP_ORDER.map(t5).filter(_temp3);
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t16;
    let t17;
    let t6;
    let t7;
    let t8;
    let t9;
    if ($[25] !== environment || $[26] !== items || $[27] !== payments.activeProvider || $[28] !== payments.configuredProvider || $[29] !== payments.testMode || $[30] !== payments.warnings.length || $[31] !== t) {
      const counts = {
        connected: items.filter(_temp4).length,
        attention: items.filter(_temp5).length
      };
      t2 = "flex flex-col gap-6";
      const t18 = payments.warnings.length > 0 && "border-amber-500/50 bg-amber-500/5";
      if ($[45] !== t18) {
        t15 = cn("flex flex-col gap-3 rounded-lg border p-5", t18);
        $[45] = t18;
        $[46] = t15;
      } else {
        t15 = $[46];
      }
      t16 = "payments-summary";
      t17 = "integrations-payments";
      t11 = "flex flex-wrap items-center gap-x-6 gap-y-2";
      let t19;
      if ($[47] !== t) {
        t19 = t("integrations.overview.activeProvider");
        $[47] = t;
        $[48] = t19;
      } else {
        t19 = $[48];
      }
      let t20;
      if ($[49] !== t19) {
        t20 = <span className="text-xs text-muted-foreground">{t19}</span>;
        $[49] = t19;
        $[50] = t20;
      } else {
        t20 = $[50];
      }
      const t21 = `integrations.overview.providerName.${payments.activeProvider}`;
      let t22;
      if ($[51] !== payments.activeProvider || $[52] !== t || $[53] !== t21) {
        t22 = t(t21, {
          defaultValue: payments.activeProvider
        });
        $[51] = payments.activeProvider;
        $[52] = t;
        $[53] = t21;
        $[54] = t22;
      } else {
        t22 = $[54];
      }
      let t23;
      if ($[55] !== payments.testMode || $[56] !== t) {
        t23 = payments.testMode && <Badge variant="outline" className="ml-2 align-middle">{t("integrations.overview.testMode")}</Badge>;
        $[55] = payments.testMode;
        $[56] = t;
        $[57] = t23;
      } else {
        t23 = $[57];
      }
      let t24;
      if ($[58] !== t22 || $[59] !== t23) {
        t24 = <span className="text-lg font-semibold">{t22}{t23}</span>;
        $[58] = t22;
        $[59] = t23;
        $[60] = t24;
      } else {
        t24 = $[60];
      }
      if ($[61] !== t20 || $[62] !== t24) {
        t12 = <div className="flex flex-col">{t20}{t24}</div>;
        $[61] = t20;
        $[62] = t24;
        $[63] = t12;
      } else {
        t12 = $[63];
      }
      let t25;
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = <span className="text-xs text-muted-foreground">PAYMENT_PROVIDER</span>;
        $[64] = t25;
      } else {
        t25 = $[64];
      }
      if ($[65] !== payments.configuredProvider) {
        t13 = <div className="flex flex-col">{t25}<code className="text-sm">{payments.configuredProvider}</code></div>;
        $[65] = payments.configuredProvider;
        $[66] = t13;
      } else {
        t13 = $[66];
      }
      let t26;
      if ($[67] !== t) {
        t26 = t("integrations.overview.environment");
        $[67] = t;
        $[68] = t26;
      } else {
        t26 = $[68];
      }
      let t27;
      if ($[69] !== t26) {
        t27 = <span className="text-xs text-muted-foreground">{t26}</span>;
        $[69] = t26;
        $[70] = t27;
      } else {
        t27 = $[70];
      }
      const t28 = `integrations.overview.env.${environment}`;
      let t29;
      if ($[71] !== environment || $[72] !== t || $[73] !== t28) {
        t29 = t(t28, {
          defaultValue: environment
        });
        $[71] = environment;
        $[72] = t;
        $[73] = t28;
        $[74] = t29;
      } else {
        t29 = $[74];
      }
      let t30;
      if ($[75] !== t29) {
        t30 = <span className="text-sm">{t29}</span>;
        $[75] = t29;
        $[76] = t30;
      } else {
        t30 = $[76];
      }
      if ($[77] !== t27 || $[78] !== t30) {
        t14 = <div className="flex flex-col">{t27}{t30}</div>;
        $[77] = t27;
        $[78] = t30;
        $[79] = t14;
      } else {
        t14 = $[79];
      }
      t8 = "ml-auto flex gap-2 text-sm text-muted-foreground";
      t9 = <span>{t("integrations.overview.countConnected", {
          count: counts.connected
        })}</span>;
      if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <span>·</span>;
        $[80] = t10;
      } else {
        t10 = $[80];
      }
      t6 = counts.attention > 0 ? "text-destructive" : undefined;
      t7 = t("integrations.overview.countAttention", {
        count: counts.attention
      });
      $[25] = environment;
      $[26] = items;
      $[27] = payments.activeProvider;
      $[28] = payments.configuredProvider;
      $[29] = payments.testMode;
      $[30] = payments.warnings.length;
      $[31] = t;
      $[32] = t10;
      $[33] = t11;
      $[34] = t12;
      $[35] = t13;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
      $[39] = t17;
      $[40] = t2;
      $[41] = t6;
      $[42] = t7;
      $[43] = t8;
      $[44] = t9;
    } else {
      t10 = $[32];
      t11 = $[33];
      t12 = $[34];
      t13 = $[35];
      t14 = $[36];
      t15 = $[37];
      t16 = $[38];
      t17 = $[39];
      t2 = $[40];
      t6 = $[41];
      t7 = $[42];
      t8 = $[43];
      t9 = $[44];
    }
    let t18;
    if ($[81] !== t6 || $[82] !== t7) {
      t18 = <span className={t6}>{t7}</span>;
      $[81] = t6;
      $[82] = t7;
      $[83] = t18;
    } else {
      t18 = $[83];
    }
    let t19;
    if ($[84] !== t10 || $[85] !== t18 || $[86] !== t8 || $[87] !== t9) {
      t19 = <div className={t8}>{t9}{t10}{t18}</div>;
      $[84] = t10;
      $[85] = t18;
      $[86] = t8;
      $[87] = t9;
      $[88] = t19;
    } else {
      t19 = $[88];
    }
    let t20;
    if ($[89] !== t11 || $[90] !== t12 || $[91] !== t13 || $[92] !== t14 || $[93] !== t19) {
      t20 = <div className={t11}>{t12}{t13}{t14}{t19}</div>;
      $[89] = t11;
      $[90] = t12;
      $[91] = t13;
      $[92] = t14;
      $[93] = t19;
      $[94] = t20;
    } else {
      t20 = $[94];
    }
    let t21;
    if ($[95] !== payments.warnings) {
      t21 = payments.warnings.map(_temp6);
      $[95] = payments.warnings;
      $[96] = t21;
    } else {
      t21 = $[96];
    }
    if ($[97] !== t15 || $[98] !== t16 || $[99] !== t17 || $[100] !== t20 || $[101] !== t21) {
      t3 = <section className={t15} data-testid={t16} data-tour={t17}>{t20}{t21}</section>;
      $[97] = t15;
      $[98] = t16;
      $[99] = t17;
      $[100] = t20;
      $[101] = t21;
      $[102] = t3;
    } else {
      t3 = $[102];
    }
    let t22;
    if ($[103] !== canManage || $[104] !== t) {
      t22 = (t23, index) => {
        const {
          group: group_0,
          items: groupItems
        } = t23;
        return <section key={group_0} className="flex flex-col gap-3" data-tour={index === 0 ? "integrations-cards" : undefined}><h2 className="text-sm font-semibold uppercase text-muted-foreground">{t(`integrations.overview.group.${group_0}`)}</h2><div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">{groupItems.map(item_0 => <IntegrationCard key={item_0.key} item={item_0} canManage={canManage} />)}</div></section>;
      };
      $[103] = canManage;
      $[104] = t;
      $[105] = t22;
    } else {
      t22 = $[105];
    }
    t4 = groups.map(t22);
    $[12] = canManage;
    $[13] = environment;
    $[14] = items;
    $[15] = payments.activeProvider;
    $[16] = payments.configuredProvider;
    $[17] = payments.testMode;
    $[18] = payments.warnings;
    $[19] = t;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t2 = $[20];
    t3 = $[21];
    t4 = $[22];
  }
  let t5;
  if ($[106] !== items) {
    t5 = <InfrastructureCostsSection items={items} />;
    $[106] = items;
    $[107] = t5;
  } else {
    t5 = $[107];
  }
  let t6;
  if ($[108] !== t2 || $[109] !== t3 || $[110] !== t4 || $[111] !== t5) {
    t6 = <div className={t2}>{t3}{t4}{t5}</div>;
    $[108] = t2;
    $[109] = t3;
    $[110] = t4;
    $[111] = t5;
    $[112] = t6;
  } else {
    t6 = $[112];
  }
  return t6;
}
function _temp6(warning) {
  return <p key={warning} className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-sm text-amber-900 dark:text-amber-200"><AlertTriangle className="mt-0.5 size-4 shrink-0" />{warning}</p>;
}
function _temp5(i_0) {
  return i_0.status === "no_keys" || i_0.status === "test_mode";
}
function _temp4(i) {
  return i.status === "connected";
}
function _temp3(g) {
  return g.items.length > 0;
}
function _temp2() {
  return api.integrations.overview();
}
function _temp(s) {
  return s.user;
}
function IntegrationCard(t0) {
  const $ = _c(77);
  const {
    item,
    canManage
  } = t0;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [result, setResult] = useState(null);
  const [checkError, setCheckError] = useState(null);
  let t1;
  if ($[0] !== item.key) {
    t1 = () => api.integrations.check(item.key);
    $[0] = item.key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setResult(null);
      setCheckError(null);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== queryClient) {
    t3 = res => {
      setResult(res);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.overview
      });
      queryClient.invalidateQueries({
        queryKey: ["integrations", "events"]
      });
    };
    $[3] = queryClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => setCheckError(e instanceof Error ? e.message : String(e));
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t3) {
    t5 = {
      mutationFn: t1,
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[6] = t1;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const check = useMutation(t5);
  let t6;
  if ($[9] !== item.settingsLabel || $[10] !== item.settingsPath) {
    t6 = item.settingsPath ? item.settingsPath.startsWith("/integrations#") ? <a href={item.settingsPath.replace("/integrations", "")} className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline">{item.settingsLabel}</a> : <Link to={item.settingsPath} className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline">{item.settingsLabel}<ExternalLink className="size-3" /></Link> : null;
    $[9] = item.settingsLabel;
    $[10] = item.settingsPath;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const settingsLink = t6;
  const t7 = item.status === "not_connected" && "border-dashed";
  let t8;
  if ($[12] !== t7) {
    t8 = cn("flex min-w-0 flex-col gap-2 rounded-lg border p-4", t7);
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `integration-${item.key}`;
  let t10;
  if ($[14] !== item.title) {
    t10 = <span className="font-medium">{item.title}</span>;
    $[14] = item.title;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = STATUS_STYLE[item.status];
  let t12;
  if ($[16] !== t11) {
    t12 = cn("rounded-md border px-2 py-0.5 text-xs font-medium", t11);
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = `integrations.overview.status.${item.status}`;
  let t14;
  if ($[18] !== t || $[19] !== t13) {
    t14 = t(t13);
    $[18] = t;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t12 || $[22] !== t14) {
    t15 = <span className={t12}>{t14}</span>;
    $[21] = t12;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== item.active || $[25] !== item.group || $[26] !== item.key || $[27] !== t) {
    t16 = item.active && item.group === "payments" && item.key !== "balance" && <Badge>{t("integrations.overview.acceptsPayments")}</Badge>;
    $[24] = item.active;
    $[25] = item.group;
    $[26] = item.key;
    $[27] = t;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t10 || $[30] !== t15 || $[31] !== t16) {
    t17 = <div className="flex flex-wrap items-center gap-2">{t10}{t15}{t16}</div>;
    $[29] = t10;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== item.statusNote) {
    t18 = <p className="text-sm">{item.statusNote}</p>;
    $[33] = item.statusNote;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== item.impact) {
    t19 = item.impact && <p className="text-xs text-muted-foreground">{item.impact}</p>;
    $[35] = item.impact;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== item.missing || $[38] !== t) {
    t20 = item.missing.length > 0 && <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs"><span className="text-muted-foreground">{t("integrations.overview.missing")} </span>{item.missing.map(_temp7)}</p>;
    $[37] = item.missing;
    $[38] = t;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== item.details) {
    t21 = item.details.length > 0 && <dl className="grid gap-1 text-sm">{item.details.map(_temp8)}</dl>;
    $[40] = item.details;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== canManage || $[43] !== item) {
    t22 = item.key === "metrika" && canManage && <MetrikaEditor item={item} />;
    $[42] = canManage;
    $[43] = item;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== item.key || $[46] !== item.lastError || $[47] !== item.lastErrorAt || $[48] !== item.lastOkAt || $[49] !== item.monthlyFee || $[50] !== t) {
    t23 = !NO_ACTIVITY.has(item.key) && <dl className="grid gap-1 border-t pt-2 text-xs"><div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("integrations.overview.lastOk")}</dt><dd>{item.lastOkAt ? formatDateTime(item.lastOkAt) : t("integrations.overview.noData")}</dd></div>{item.lastErrorAt && <div className="flex flex-col gap-0.5"><div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("integrations.overview.lastError")}</dt><dd className="text-destructive">{formatDateTime(item.lastErrorAt)}</dd></div>{item.lastError && <p className="break-words text-destructive">{item.lastError}</p>}</div>}{item.monthlyFee != null && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("integrations.overview.fee")}</dt><dd>{formatPrice(item.monthlyFee)}</dd></div>}</dl>;
    $[45] = item.key;
    $[46] = item.lastError;
    $[47] = item.lastErrorAt;
    $[48] = item.lastOkAt;
    $[49] = item.monthlyFee;
    $[50] = t;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== canManage || $[53] !== check || $[54] !== item.checkable || $[55] !== t) {
    t24 = canManage && item.checkable && <Button size="sm" variant="outline" onClick={() => check.mutate()} disabled={check.isPending}>{check.isPending && <Loader2 className="size-3 animate-spin" />}{t("integrations.overview.check")}</Button>;
    $[52] = canManage;
    $[53] = check;
    $[54] = item.checkable;
    $[55] = t;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== settingsLink || $[58] !== t24) {
    t25 = <div className="mt-auto flex flex-wrap items-center gap-3 pt-1">{t24}{settingsLink}</div>;
    $[57] = settingsLink;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== result) {
    t26 = result && <p className={cn("flex items-start gap-1.5 text-xs", result.ok ? "text-emerald-700 dark:text-emerald-400" : "text-destructive")} role="status">{result.ok ? <CheckCircle2 className="size-3.5 shrink-0" /> : <XCircle className="size-3.5 shrink-0" />}{result.message}</p>;
    $[60] = result;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== checkError) {
    t27 = checkError && <p className="text-xs text-destructive" role="alert">{checkError}</p>;
    $[62] = checkError;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t20 || $[68] !== t21 || $[69] !== t22 || $[70] !== t23 || $[71] !== t25 || $[72] !== t26 || $[73] !== t27 || $[74] !== t8 || $[75] !== t9) {
    t28 = <article className={t8} data-testid={t9}>{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t25}{t26}{t27}</article>;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t8;
    $[75] = t9;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  return t28;
}
function _temp8(detail) {
  return <div key={detail.label} className="flex justify-between gap-3"><dt className="text-muted-foreground">{detail.label}</dt><dd className="min-w-0 text-right [overflow-wrap:anywhere]">{detail.value}</dd></div>;
}
function _temp7(name) {
  return <code key={name} className="mr-1 inline-block break-all rounded bg-muted px-1">{name}</code>;
}
function MetrikaEditor(t0) {
  const $ = _c(32);
  const {
    item
  } = t0;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const current = item.details.find(_temp9)?.value;
  const [value, setValue] = useState(item.configured && current ? current : "");
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== value) {
    t1 = () => api.integrations.setMetrikaCounter(value.trim() || null);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = () => {
      setError(null);
      queryClient.invalidateQueries({
        queryKey: integrationKeys.overview
      });
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => setError(e instanceof Error ? e.message : String(e));
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const save = useMutation(t4);
  let t5;
  if ($[8] !== save) {
    t5 = e_0 => {
      e_0.preventDefault();
      save.mutate();
    };
    $[8] = save;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("integrations.overview.metrikaLabel");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <Label htmlFor="metrika-counter" className="text-xs">{t6}</Label>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_1 => setValue(e_1.target.value.replace(/\D/g, ""));
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== value) {
    t9 = <Input id="metrika-counter" inputMode="numeric" className="h-8 w-40" value={value} placeholder="12345678" onChange={t8} />;
    $[15] = value;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <div className="flex flex-col gap-1">{t7}{t9}</div>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = save.isPending;
  let t12;
  if ($[20] !== t) {
    t12 = t("integrations.overview.save");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== save.isPending || $[23] !== t12) {
    t13 = <Button size="sm" type="submit" disabled={t11}>{t12}</Button>;
    $[22] = save.isPending;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== error) {
    t14 = error && <p className="w-full text-xs text-destructive">{error}</p>;
    $[25] = error;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t10 || $[28] !== t13 || $[29] !== t14 || $[30] !== t5) {
    t15 = <form className="flex flex-wrap items-end gap-2" onSubmit={t5}>{t10}{t13}{t14}</form>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}
function _temp9(d) {
  return d.label === "\u041D\u043E\u043C\u0435\u0440 \u0441\u0447\u0451\u0442\u0447\u0438\u043A\u0430";
}
const parseRub = (raw: string): number | null => {
  const clean = raw.replace(/\s/g, "").replace(",", ".");
  if (!clean) return null;
  const n = Number(clean);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
};

/**
 * «Инфраструктура и расходы»: серверы и абонплаты интеграций в месяц.
 * Цифры живут в настройках площадки; править может только администратор
 * (владелец) — остальные видят итог.
 */
function InfrastructureCostsSection(t0) {
  const $ = _c(36);
  const {
    items
  } = t0;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(_temp0);
  const isOwner = user?.role === "admin";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: integrationKeys.costs,
      queryFn: _temp1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const costs = useQuery(t1);
  const [editing, setEditing] = useState(false);
  let t2;
  if ($[1] !== t) {
    t2 = t("integrations.costs.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <h2 className="text-lg font-semibold">{t2}</h2>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("integrations.costs.intro");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-sm text-muted-foreground">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="flex flex-col gap-1">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== costs.data || $[13] !== editing || $[14] !== isOwner || $[15] !== t) {
    t7 = isOwner && !editing && costs.data && <Button variant="outline" size="sm" onClick={() => setEditing(true)}>{t("integrations.costs.edit")}</Button>;
    $[12] = costs.data;
    $[13] = editing;
    $[14] = isOwner;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex flex-wrap items-center justify-between gap-3">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== costs.error || $[21] !== costs.isError) {
    t9 = costs.isError && <p className="text-sm text-destructive">{costs.error instanceof Error ? costs.error.message : String(costs.error)}</p>;
    $[20] = costs.error;
    $[21] = costs.isError;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== costs.data || $[24] !== editing || $[25] !== items || $[26] !== queryClient) {
    t10 = costs.data && (editing ? <CostsForm costs={costs.data} items={items} onDone={next => {
      if (next) {
        queryClient.setQueryData(integrationKeys.costs, next);
        queryClient.invalidateQueries({
          queryKey: integrationKeys.overview
        });
      }
      setEditing(false);
    }} /> : <CostsTable costs={costs.data} items={items} />);
    $[23] = costs.data;
    $[24] = editing;
    $[25] = items;
    $[26] = queryClient;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== isOwner || $[29] !== t) {
    t11 = !isOwner && <p className="text-xs text-muted-foreground">{t("integrations.costs.ownerOnly")}</p>;
    $[28] = isOwner;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t8 || $[34] !== t9) {
    t12 = <section className="flex flex-col gap-4 rounded-lg border p-5" data-testid="infra-costs" data-tour="integrations-costs">{t8}{t9}{t10}{t11}</section>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t8;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
function _temp1() {
  return api.integrations.costs();
}
function _temp0(s) {
  return s.user;
}
function CostsTable(t0) {
  const $ = _c(59);
  const {
    costs,
    items
  } = t0;
  const {
    t
  } = useTranslation();
  let feeRows;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== costs.fees || $[1] !== costs.servers || $[2] !== costs.serversNote || $[3] !== items || $[4] !== t) {
    let t5;
    if ($[10] !== costs.fees) {
      t5 = item => costs.fees[item.key] != null;
      $[10] = costs.fees;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    feeRows = items.filter(t5);
    t4 = "flex flex-col gap-2";
    t3 = "w-full max-w-xl text-sm";
    let t6;
    if ($[12] !== t) {
      t6 = t("integrations.costs.servers");
      $[12] = t;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== costs.serversNote) {
      t7 = costs.serversNote && <span className="block text-xs text-muted-foreground">{costs.serversNote}</span>;
      $[14] = costs.serversNote;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t6 || $[17] !== t7) {
      t8 = <td className="py-2 pr-4">{t6}{t7}</td>;
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== costs.servers || $[20] !== t) {
      t9 = costs.servers != null ? formatPrice(costs.servers) : t("integrations.costs.notSet");
      $[19] = costs.servers;
      $[20] = t;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== t9) {
      t10 = <td className="py-2 text-right tabular-nums">{t9}</td>;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    if ($[24] !== t10 || $[25] !== t8) {
      t1 = <tr className="border-b">{t8}{t10}</tr>;
      $[24] = t10;
      $[25] = t8;
      $[26] = t1;
    } else {
      t1 = $[26];
    }
    let t11;
    if ($[27] !== costs.fees) {
      t11 = item_0 => <tr key={item_0.key} className="border-b"><td className="py-2 pr-4">{item_0.title}</td><td className="py-2 text-right tabular-nums">{formatPrice(costs.fees[item_0.key] ?? 0)}</td></tr>;
      $[27] = costs.fees;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    t2 = feeRows.map(t11);
    $[0] = costs.fees;
    $[1] = costs.servers;
    $[2] = costs.serversNote;
    $[3] = items;
    $[4] = t;
    $[5] = feeRows;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    feeRows = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[29] !== t) {
    t5 = t("integrations.costs.total");
    $[29] = t;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== t5) {
    t6 = <td className="py-2 pr-4 font-semibold">{t5}</td>;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== costs.totalMonthly) {
    t7 = formatPrice(costs.totalMonthly);
    $[33] = costs.totalMonthly;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t7) {
    t8 = <td className="py-2 text-right font-semibold tabular-nums" data-testid="infra-total">{t7}</td>;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t6 || $[38] !== t8) {
    t9 = <tr>{t6}{t8}</tr>;
    $[37] = t6;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== t1 || $[41] !== t2 || $[42] !== t9) {
    t10 = <tbody>{t1}{t2}{t9}</tbody>;
    $[40] = t1;
    $[41] = t2;
    $[42] = t9;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t3) {
    t11 = <table className={t3}>{t10}</table>;
    $[44] = t10;
    $[45] = t3;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== feeRows.length || $[48] !== t) {
    t12 = feeRows.length === 0 && <p className="text-xs text-muted-foreground">{t("integrations.costs.noFees")}</p>;
    $[47] = feeRows.length;
    $[48] = t;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== costs.updatedAt || $[51] !== costs.updatedBy || $[52] !== t) {
    t13 = costs.updatedAt && <p className="text-xs text-muted-foreground">{t("integrations.costs.updated", {
        when: formatDateTime(costs.updatedAt),
        who: costs.updatedBy ?? t("integrations.costs.byMigration")
      })}</p>;
    $[50] = costs.updatedAt;
    $[51] = costs.updatedBy;
    $[52] = t;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== t11 || $[55] !== t12 || $[56] !== t13 || $[57] !== t4) {
    t14 = <div className={t4}>{t11}{t12}{t13}</div>;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
    $[57] = t4;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  return t14;
}
function CostsForm(t0) {
  const $ = _c(78);
  const {
    costs,
    items,
    onDone
  } = t0;
  const {
    t
  } = useTranslation();
  const [servers, setServers] = useState(costs.servers != null ? String(costs.servers) : "");
  const [note, setNote] = useState(costs.serversNote);
  let t1;
  if ($[0] !== items) {
    t1 = items.filter(_temp10);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const billable = t1;
  let t2;
  if ($[2] !== billable || $[3] !== costs.fees) {
    t2 = () => Object.fromEntries(billable.map(item_0 => [item_0.key, costs.fees[item_0.key] != null ? String(costs.fees[item_0.key]) : ""]));
    $[2] = billable;
    $[3] = costs.fees;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [fees, setFees] = useState(t2);
  const [error, setError] = useState(null);
  let t3;
  if ($[5] !== fees || $[6] !== note || $[7] !== servers || $[8] !== t) {
    t3 = () => {
      const serversValue = parseRub(servers);
      const feeValues = {};
      for (const [key, raw] of Object.entries(fees)) {
        feeValues[key] = parseRub(raw);
      }
      if (Number.isNaN(serversValue) || Object.values(feeValues).some(_temp11)) {
        throw new Error(t("integrations.costs.invalid"));
      }
      return api.integrations.updateCosts({
        servers: serversValue,
        serversNote: note.trim(),
        fees: feeValues
      });
    };
    $[5] = fees;
    $[6] = note;
    $[7] = servers;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onDone) {
    t4 = next => onDone(next);
    $[10] = onDone;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => setError(e instanceof Error ? e.message : String(e));
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4) {
    t6 = {
      mutationFn: t3,
      onSuccess: t4,
      onError: t5
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const save = useMutation(t6);
  let t7;
  if ($[16] !== save) {
    t7 = e_0 => {
      e_0.preventDefault();
      setError(null);
      save.mutate();
    };
    $[16] = save;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("integrations.costs.serversNote");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <Label htmlFor="cost-servers-note">{t8}</Label>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => setNote(e_1.target.value);
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== note) {
    t11 = <Input id="cost-servers-note" value={note} onChange={t10} maxLength={300} />;
    $[23] = note;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <div className="flex flex-col gap-1">{t9}{t11}</div>;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t) {
    t13 = t("integrations.costs.serversMonthly");
    $[28] = t;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <Label htmlFor="cost-servers">{t13}</Label>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e_2 => setServers(e_2.target.value);
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== servers) {
    t16 = <Input id="cost-servers" inputMode="decimal" value={servers} onChange={t15} />;
    $[33] = servers;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t16) {
    t17 = <div className="flex flex-col gap-1">{t14}{t16}</div>;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t12 || $[39] !== t17) {
    t18 = <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">{t12}{t17}</div>;
    $[38] = t12;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t("integrations.costs.feesHint");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19) {
    t20 = <p className="text-xs text-muted-foreground">{t19}</p>;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== billable || $[46] !== fees || $[47] !== t) {
    let t22;
    if ($[49] !== fees || $[50] !== t) {
      t22 = item_1 => <div key={item_1.key} className="grid items-center gap-2 sm:grid-cols-[1fr_12rem]"><Label htmlFor={`fee-${item_1.key}`} className="font-normal">{item_1.title}</Label><Input id={`fee-${item_1.key}`} inputMode="decimal" placeholder={t("integrations.costs.feePlaceholder")} value={fees[item_1.key] ?? ""} onChange={e_3 => setFees(prev => ({
          ...prev,
          [item_1.key]: e_3.target.value
        }))} /></div>;
      $[49] = fees;
      $[50] = t;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    t21 = billable.map(t22);
    $[45] = billable;
    $[46] = fees;
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[52] !== t21) {
    t22 = <div className="grid gap-2">{t21}</div>;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== error) {
    t23 = error && <p className="text-sm text-destructive">{error}</p>;
    $[54] = error;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const t24 = save.isPending;
  let t25;
  if ($[56] !== t) {
    t25 = t("integrations.overview.save");
    $[56] = t;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== save.isPending || $[59] !== t25) {
    t26 = <Button type="submit" disabled={t24}>{t25}</Button>;
    $[58] = save.isPending;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== onDone) {
    t27 = () => onDone(null);
    $[61] = onDone;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t) {
    t28 = t("integrations.costs.cancel");
    $[63] = t;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t27 || $[66] !== t28) {
    t29 = <Button type="button" variant="outline" onClick={t27}>{t28}</Button>;
    $[65] = t27;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t26 || $[69] !== t29) {
    t30 = <div className="flex gap-2">{t26}{t29}</div>;
    $[68] = t26;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t18 || $[72] !== t20 || $[73] !== t22 || $[74] !== t23 || $[75] !== t30 || $[76] !== t7) {
    t31 = <form className="flex max-w-xl flex-col gap-3" onSubmit={t7}>{t18}{t20}{t22}{t23}{t30}</form>;
    $[71] = t18;
    $[72] = t20;
    $[73] = t22;
    $[74] = t23;
    $[75] = t30;
    $[76] = t7;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  return t31;
}
function _temp11(v) {
  return Number.isNaN(v);
}
function _temp10(item) {
  return item.status !== "not_connected";
}